import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Briefcase, Loader2, Languages, Award, FileText } from "lucide-react";
import { Layout } from "@/components/Layout";
import { AvailabilityBadge } from "@/components/AvailabilityBadge";
import { ShareMenu } from "@/components/ShareMenu";
import { Progress } from "@/components/ui/progress";
import { api } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

export default function PublicProfile() {
  const { slug } = useParams();
  const { t } = useLang();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { api.get(`/candidates/${slug}`).then((r) => setC(r.data)).catch(() => setNotFound(true)); }, [slug]);

  if (notFound) return <Layout><p className="py-32 text-center text-slate-400">Profile not found or private.</p></Layout>;
  if (!c) return <Layout><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div></Layout>;

  const shareUrl = `${window.location.origin}/u/${c.slug || c.id}`;
  const shareMsg = `I am currently available for work. View my professional profile on GetJobs.Today:`;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="gjt-card overflow-hidden">
          <div className="h-24 bg-gradient-to-r from-navy to-brand-blue" />
          <div className="px-6 sm:px-8 pb-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 -mt-12">
              <div className="flex items-end gap-4">
                <img src={c.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name)}&background=0A2472&color=fff&size=160`}
                  alt={c.name} className="h-24 w-24 rounded-2xl object-cover ring-4 ring-white" />
                <div className="pb-1">
                  <h1 className="font-display text-2xl font-extrabold text-navy">{c.name}</h1>
                  <p className="font-medium text-brand-blue">{c.job_title}</p>
                </div>
              </div>
              <ShareMenu url={shareUrl} text={shareMsg} label={t("share_profile")} testid="share-profile" />
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <AvailabilityBadge status={c.availability} size="lg" />
              <span className="flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-4 w-4" /> {c.city}, {c.country}</span>
              <span className="flex items-center gap-1 text-sm text-slate-500"><Briefcase className="h-4 w-4" /> {c.experience_years || 0}+ {t("years")} {t("experience")}</span>
            </div>

            {c.bio && <p className="mt-5 text-sm leading-relaxed text-slate-700">{c.bio}</p>}

            <div className="mt-6 grid gap-6 sm:grid-cols-3">
              <div className="sm:col-span-2 space-y-6">
                <div>
                  <h3 className="font-display text-sm font-bold uppercase tracking-wide text-slate-400">Skills</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(c.skills || []).map((s) => <span key={s} className="rounded-full bg-brand-bluelight px-3 py-1 text-sm font-medium text-brand-blue">{s}</span>)}
                  </div>
                </div>
                <div>
                  <h3 className="font-display text-sm font-bold uppercase tracking-wide text-slate-400">Work preferences</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(c.work_types || []).map((w) => <span key={w} className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium capitalize text-slate-600">{w}</span>)}
                  </div>
                </div>
                {c.expected_salary && (
                  <div>
                    <h3 className="font-display text-sm font-bold uppercase tracking-wide text-slate-400">Expectations</h3>
                    <p className="mt-2 font-semibold text-navy">{c.expected_salary}</p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-navy">{t("profile_strength")}</span>
                    <span className="font-bold text-emerald-600">{c.completeness}%</span>
                  </div>
                  <Progress value={c.completeness} className="mt-2 h-2" />
                </div>
                <div className="rounded-xl border border-slate-200 p-4 text-sm space-y-2 text-slate-600">
                  <div className="flex items-center gap-2"><Languages className="h-4 w-4 text-brand-blue" /> {c.language || "English"}</div>
                  {c.portfolio && <div className="flex items-center gap-2"><Award className="h-4 w-4 text-brand-blue" /> <a href={c.portfolio} className="text-brand-blue hover:underline" target="_blank" rel="noreferrer">Portfolio</a></div>}
                  {c.resume_url && <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-brand-blue" /> Resume available</div>}
                  <div className="text-xs text-slate-400">{c.engagement?.views || 0} profile views</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
